import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Eyebrow } from "../components/Primitives";
import { useAccount } from "../account/AccountContext";
import { formatAuthError } from "../lib/accountsApi";
import AppShell from "../components/AppShell";
import PasswordInput from "../components/PasswordInput";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const { resetPassword } = useAccount();
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) setError("Invalid or missing reset link.");
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw !== pw2) { setError("Passwords don't match."); return; }
        if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
        setSubmitting(true);
        setError(null);
        try {
            await resetPassword(token, pw);
            toast.success("Password updated — you're signed in.");
            navigate("/");
        } catch (err) {
            setError(formatAuthError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col px-7 py-10" data-testid="reset-password-screen">
                <Eyebrow>Set new password</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-tight">
                    Pick a <span className="italic">new password.</span>
                </h1>

                <form onSubmit={submit} className="mt-7 space-y-4">
                    <Field label="New password">
                        <PasswordInput
                            testId="reset-pw"
                            autoComplete="new-password"
                            value={pw}
                            onChange={e => setPw(e.target.value)}
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                        />
                    </Field>
                    <Field label="Confirm new password">
                        <PasswordInput
                            testId="reset-pw2"
                            autoComplete="new-password"
                            value={pw2}
                            onChange={e => setPw2(e.target.value)}
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                        />
                    </Field>
                    {error && (
                        <div data-testid="reset-error" className="text-xs text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">
                            {error}
                        </div>
                    )}
                    <Btn data-testid="reset-submit" type="submit" disabled={submitting || !pw || !pw2 || !token}>
                        {submitting ? "Updating…" : "Update password"}
                    </Btn>
                </form>

                <div className="mt-6 text-center">
                    <Link to="/login" className="text-[11px] tracking-[0.24em] uppercase text-burgundy underline underline-offset-4">
                        ← Back to sign in
                    </Link>
                </div>
            </div>
        </AppShell>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">{label}</div>
            {children}
        </label>
    );
}
